/**
 * Background service for monitoring document import batches
 * and showing browser notifications when complete
 */
class DocumentImportNotificationService {
  constructor(dataProvider, notificationProvider, t) {
    this.dataProvider = dataProvider
    this.notificationProvider = notificationProvider
    this.t = t
    this.activeBatches = new Map() // batchId -> { pollInterval, startTime, fileName }
    this.completedBatches = new Map() // batchId -> result
    this.pollingInterval = 5000 // 5 seconds
    this.isDialogOpen = false // Track if import dialog is currently open
  }

  /**
   * Set dialog open state
   * Call this from the dialog component when it opens/closes
   */
  setDialogOpen(isOpen) {
    this.isDialogOpen = isOpen
  }

  /**
   * Request browser notification permission
   */
  async requestNotificationPermission() {
    if (!('Notification' in window)) {
      console.warn(
        '[NotificationService] Browser does not support notifications'
      )
      return false
    }

    if (Notification.permission === 'granted') {
      return true
    }

    if (Notification.permission !== 'denied') {
      const permission = await Notification.requestPermission()
      return permission === 'granted'
    }

    return false
  }

  /**
   * Register a batch for background monitoring
   */
  registerBatch(batchId, fileName) {
    // Don't register if already monitoring
    if (this.activeBatches.has(batchId)) {
      return
    }

    // Start polling
    const pollInterval = setInterval(() => {
      this.checkBatchStatus(batchId)
    }, this.pollingInterval)

    this.activeBatches.set(batchId, {
      pollInterval,
      startTime: Date.now(),
      fileName,
    })

    // Save to localStorage for persistence across page reloads
    this.saveActiveBatches()

    // Request notification permission if not already granted
    this.requestNotificationPermission()
  }

  /**
   * Check status of a batch
   */
  async checkBatchStatus(batchId) {
    try {
      const timestamp = Date.now()
      const response = await this.dataProvider.get(
        `chassis/documents/bulk-import/status/${batchId}`,
        {
          query: { _t: timestamp }, // Cache-busting
        }
      )

      const result = response.data

      if (result.status === 'completed' || result.status === 'failed') {
        this.handleBatchComplete(batchId, result)
      }
    } catch (error) {
      console.error(
        `[NotificationService] ❌ Error checking batch ${batchId}:`,
        error
      )

      // If batch not found (404), stop monitoring
      if (error?.response?.status === 404) {
        this.stopMonitoring(batchId)
      }
    }
  }

  /**
   * Handle batch completion
   */
  handleBatchComplete(batchId, result) {
    const batchInfo = this.activeBatches.get(batchId)
    if (!batchInfo) return

    // Stop polling
    this.stopMonitoring(batchId)

    // Store completed result
    this.completedBatches.set(batchId, result)
    this.saveCompletedBatches()

    // Show browser notification (always show, even if dialog is open - user might be on another tab)
    this.showNotification(batchId, result, batchInfo.fileName)

    // Show in-app notification only if dialog is not already open
    // (If dialog is open, user is already viewing the results)
    if (this.isDialogOpen) {
      return
    }

    // Show in-app notification with click handler
    const handleNotificationClick = () => {
      window.dispatchEvent(
        new CustomEvent('openDocumentImportResults', {
          detail: { batchId, result },
        })
      )
    }

    // Determine notification severity based on actual results, not just status
    const successCount = result.progress?.totalDocumentsCreated || 0
    const errorCount = result.progress?.errorCount || 0

    // Priority 1: If documents were created successfully
    if (successCount > 0 && errorCount === 0) {
      // Green - Complete success
      this.notificationProvider.showNotification(
        'success',
        this.t('chassis-document-import.background-complete', {
          count: successCount,
          defaultValue: 'Document import complete: {{count}} documents created',
        }),
        {
          persist: true, // Keep visible until manually dismissed
          action: {
            label: this.t('common.view', 'View'),
            onClick: handleNotificationClick,
          },
        }
      )
    } else if (successCount > 0 && errorCount > 0) {
      // Orange - Partial success (some documents created, but has errors/unrecognized)
      this.notificationProvider.showNotification(
        'warning',
        this.t('chassis-document-import.background-complete-with-errors', {
          count: successCount,
          errors: errorCount,
          defaultValue:
            'Document import complete: {{count}} documents created, {{errors}} error(s)',
        }),
        {
          persist: true, // Keep visible until manually dismissed
          action: {
            label: this.t('common.view', 'View'),
            onClick: handleNotificationClick,
          },
        }
      )
    } else {
      // Red - Complete failure (no documents created)
      this.notificationProvider.showNotification(
        'error',
        this.t('chassis-document-import.background-failed', {
          defaultValue: 'Document import failed',
        }),
        {
          persist: true, // Keep visible until manually dismissed
          action: {
            label: this.t('common.view', 'View'),
            onClick: handleNotificationClick,
          },
        }
      )
    }
  }

  /**
   * Show browser notification
   */
  showNotification(batchId, result) {
    if (!('Notification' in window)) {
      console.warn(
        '[NotificationService] Browser does not support notifications'
      )
      return
    }

    if (Notification.permission !== 'granted') {
      console.warn(
        '[NotificationService] Notification permission not granted:',
        Notification.permission
      )
      return
    }

    const successCount = result.progress?.totalDocumentsCreated || 0
    const errorCount = result.progress?.errorCount || 0

    let title, body, icon

    // Determine notification content based on actual results, not just status
    if (successCount > 0 && errorCount === 0) {
      // Green - Complete success
      title = this.t('chassis-document-import.notification-title-success', {
        defaultValue: 'Document Import Complete',
      })
      body = this.t('chassis-document-import.notification-body-success', {
        count: successCount,
        defaultValue: '{{count}} documents were successfully imported',
      })
      icon = '✅'
    } else if (successCount > 0 && errorCount > 0) {
      // Orange - Partial success (some documents created, but has errors/unrecognized)
      title = this.t('chassis-document-import.notification-title-partial', {
        defaultValue: 'Document Import Completed with Errors',
      })
      body = this.t('chassis-document-import.notification-body-partial', {
        count: successCount,
        errors: errorCount,
        defaultValue:
          '{{count}} documents imported, {{errors}} error(s) occurred',
      })
      icon = '⚠️'
    } else {
      // Red - Complete failure (no documents created)
      title = this.t('chassis-document-import.notification-title-failed', {
        defaultValue: 'Document Import Failed',
      })
      body = this.t('chassis-document-import.notification-body-failed', {
        defaultValue: 'An error occurred during document import',
      })
      icon = '❌'
    }

    try {
      const notification = new Notification(title, {
        body,
        icon: icon,
        badge: icon,
        tag: batchId, // Prevent duplicate notifications
        requireInteraction: false, // Auto-dismiss after timeout
        data: { batchId, result }, // Store data for click handler
      })

      // Handle notification click - focus window and open dialog only if not already open
      notification.onclick = () => {
        window.focus() // Focus the window

        // Only dispatch event to open dialog if it's not already open
        if (!this.isDialogOpen) {
          window.dispatchEvent(
            new CustomEvent('openDocumentImportResults', {
              detail: { batchId, result },
            })
          )
        }

        notification.close()
      }
    } catch (error) {
      console.error('[NotificationService] Error creating notification:', error)
    }
  }

  /**
   * Stop monitoring a batch
   */
  stopMonitoring(batchId) {
    const batchInfo = this.activeBatches.get(batchId)
    if (batchInfo) {
      clearInterval(batchInfo.pollInterval)
      this.activeBatches.delete(batchId)
      this.saveActiveBatches()
    }
  }

  /**
   * Get completed batch result
   */
  getCompletedBatch(batchId) {
    return this.completedBatches.get(batchId)
  }

  /**
   * Remove completed batch from storage
   */
  clearCompletedBatch(batchId) {
    this.completedBatches.delete(batchId)
    this.saveCompletedBatches()
  }

  /**
   * Save active batches to localStorage
   */
  saveActiveBatches() {
    try {
      const batches = Array.from(this.activeBatches.entries()).map(
        ([batchId, info]) => ({
          batchId,
          startTime: info.startTime,
          fileName: info.fileName,
        })
      )
      localStorage.setItem(
        'documentImportActiveBatches',
        JSON.stringify(batches)
      )
    } catch (error) {
      console.error('[NotificationService] Error saving active batches:', error)
    }
  }

  /**
   * Save completed batches to localStorage
   */
  saveCompletedBatches() {
    try {
      const batches = Array.from(this.completedBatches.entries()).map(
        ([batchId, result]) => ({
          batchId,
          result,
          completedAt: Date.now(),
        })
      )
      localStorage.setItem(
        'documentImportCompletedBatches',
        JSON.stringify(batches)
      )
    } catch (error) {
      console.error(
        '[NotificationService] Error saving completed batches:',
        error
      )
    }
  }

  /**
   * Restore active batches from localStorage (for page reloads)
   */
  restoreActiveBatches() {
    try {
      const stored = localStorage.getItem('documentImportActiveBatches')
      if (!stored) return

      const batches = JSON.parse(stored)
      batches.forEach(({ batchId, fileName }) => {
        // Re-register each batch (will start polling again)
        this.registerBatch(batchId, fileName)
      })
    } catch (error) {
      console.error(
        '[NotificationService] Error restoring active batches:',
        error
      )
    }
  }

  /**
   * Restore completed batches from localStorage
   */
  restoreCompletedBatches() {
    try {
      const stored = localStorage.getItem('documentImportCompletedBatches')
      if (!stored) return

      const batches = JSON.parse(stored)
      const now = Date.now()
      const maxAge = 24 * 60 * 60 * 1000 // 24 hours

      batches.forEach(({ batchId, result, completedAt }) => {
        // Only restore if less than 24 hours old
        if (now - completedAt < maxAge) {
          this.completedBatches.set(batchId, result)
        }
      })

      // Clean up old entries
      this.saveCompletedBatches()
    } catch (error) {
      console.error(
        '[NotificationService] Error restoring completed batches:',
        error
      )
    }
  }

  /**
   * Initialize service (call on app startup)
   */
  initialize() {
    // Restore from localStorage
    this.restoreActiveBatches()
    this.restoreCompletedBatches()

    // Request notification permission
    this.requestNotificationPermission()
  }

  /**
   * Cleanup (call on unmount)
   */
  cleanup() {
    // Stop all polling
    this.activeBatches.forEach((_, batchId) => {
      this.stopMonitoring(batchId)
    })
  }
}

export default DocumentImportNotificationService
